import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { passportInstance, getPassportUserInfo, connectPassportProvider } from '../config/passport';
import { useAuth } from '../context/AuthContext';
import api from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

/**
 * PassportCallback Component
 *
 * This component handles the callback from Immutable Passport after authentication.
 * It processes the authentication response and redirects the user back to the application.
 * It ensures that AppKit doesn't interfere with the Passport authentication.
 */
export default function PassportCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing authentication...');
  const { updateUserContext } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('Processing Passport callback...');
        setStatus('Processing authentication response...');

        // Explicitly mark that we're using Passport for authentication
        // This will prevent AppKit from trying to auto-connect
        localStorage.setItem('wallet_provider', 'passport');

        // Clear any AppKit auto-reconnect flags
        localStorage.removeItem('wallet_reconnect_requested');

        // Process the authentication response
        const result = await passportInstance.loginCallback();
        console.log('Passport loginCallback result:', result);
        setStatus('Authentication successful, getting user info...');

        // Get user info and connect to the provider
        try {
          // Get user info from Passport
          const userInfo = await getPassportUserInfo();
          console.log('Passport user info:', userInfo);
          setStatus('User info retrieved, connecting to provider...');

          // Connect to the Passport EVM provider
          const provider = await connectPassportProvider();
          console.log('Connected to Passport provider:', provider);
          setStatus('Provider connected, getting wallet address...');

          // Get the user's address
          const accounts = await provider.request({ method: 'eth_requestAccounts' });
          const address = accounts[0];
          console.log('Passport wallet address:', address);

          if (address) {
            setStatus('Creating user session...');

            // Create a basic user object
            const timestamp = Date.now();
            const sessionExpiry = timestamp + (14 * 24 * 60 * 60 * 1000); // 14 days

            // Create base64 token string for API authentication
            const messageToSign = `Authenticate with BitPool for address ${address} at timestamp ${timestamp}`;
            const base64Token = btoa(JSON.stringify({
              address,
              timestamp,
              message: messageToSign,
              provider: 'passport',
              expiry: sessionExpiry
            }));

            // Store token in localStorage
            localStorage.setItem('token', base64Token);

            const basicUser = {
              address,
              provider: 'passport',
              passportInfo: userInfo,
              passportAddress: address,
              isConnected: true,
              timestamp,
              sessionExpiry,
              token: base64Token
            };

            // Update the auth context with basic user info
            updateUserContext(basicUser);

            // Store connection info in localStorage
            localStorage.setItem('wallet_connected', 'true');
            localStorage.setItem('wallet_address', address);
            localStorage.setItem('wallet_provider', 'passport');
            localStorage.setItem('wallet_last_connected', timestamp.toString());
            localStorage.setItem('user', JSON.stringify(basicUser));

            // Dispatch a custom event to notify other components about the authentication
            const authEvent = new CustomEvent('passport-authenticated', {
              detail: {
                address,
                provider: 'passport',
                isConnected: true
              }
            });
            window.dispatchEvent(authEvent);

            console.log('Passport authentication completed successfully');
            setStatus('Authentication complete, fetching user profile...');

            // Get user info from API
            try {
              const userApiInfo = await api.get(API_ENDPOINTS.wallet.get(address));
              const fullUser = {
                ...basicUser,
                isAdmin: userApiInfo.isAdmin || false,
                username: userApiInfo.username,
                bitpBalance: userApiInfo.bitpBalance,
                usdBalance: userApiInfo.usdBalance,
                token: base64Token
              };

              // Update localStorage and context with full user info
              localStorage.setItem('user', JSON.stringify(fullUser));
              updateUserContext(fullUser);

              // Dispatch another event with the full user info
              const fullUserEvent = new CustomEvent('passport-user-updated', {
                detail: fullUser
              });
              window.dispatchEvent(fullUserEvent);

              console.log('User profile retrieved successfully');
            } catch (apiError) {
              console.error('Error getting user profile from API:', apiError);
              // Continue with basic user info
            }
          }
        } catch (userError) {
          console.error('Error getting user info or connecting provider:', userError);
          setError(`Error connecting to Immutable: ${userError.message}`);
          // Continue with navigation even if we couldn't get user info
        }

        // Add a small delay before redirecting to ensure state updates are processed
        setTimeout(() => {
          // Redirect to home page after successful authentication
          console.log('Authentication successful, redirecting to home page...');
          setStatus('Authentication successful, redirecting...');

          // Dispatch another custom event to ensure all components are updated
          const finalAuthEvent = new CustomEvent('passport-auth-completed', {
            detail: {
              address,
              provider: 'passport',
              isConnected: true
            }
          });
          window.dispatchEvent(finalAuthEvent);

          // Use replace: true to avoid back button issues
          navigate('/', { replace: true });

          // Force a page reload after a short delay to ensure UI is updated
          // This is a fallback in case the state updates don't propagate correctly
          setTimeout(() => {
            // Check if we're on the home page and the UI hasn't updated
            if (window.location.pathname === '/' && !document.querySelector('[data-passport-connected="true"]')) {
              console.log('UI not updated after navigation, forcing reload');

              // Try one more time to update the auth state before reloading
              const lastChanceEvent = new CustomEvent('passport-auth-completed', {
                detail: {
                  address,
                  provider: 'passport',
                  isConnected: true
                }
              });
              window.dispatchEvent(lastChanceEvent);

              // If still not updated, reload the page
              if (!document.querySelector('[data-passport-connected="true"]')) {
                window.location.reload();
              }
            }
          }, 1000);
        }, 500);
      } catch (err) {
        console.error('Error processing Passport callback:', err);
        if (err.message) {
          console.error('Error message:', err.message);
        }
        if (err.stack) {
          console.error('Error stack:', err.stack);
        }

        // Provide a user-friendly error message
        let errorMessage = 'Failed to complete authentication. Please try again.';
        if (err.message && err.message.includes('popup')) {
          errorMessage = 'The login popup was blocked. Please allow popups for this site and try again.';
        } else if (err.message && err.message.includes('network')) {
          errorMessage = 'Network error connecting to Immutable. Please ensure you\'re connected to the correct network.';
        }

        setError(errorMessage);
      }
    };

    handleCallback();
  }, [navigate, updateUserContext]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-dark">
        <div className="p-6 bg-dark-lighter rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-white mb-4">Authentication Error</h2>
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-2 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-dark">
      <div className="p-6 bg-dark-lighter rounded-lg shadow-lg max-w-md w-full">
        <h2 className="text-xl font-bold text-white mb-4">Completing Authentication</h2>
        <p className="text-gray-300 mb-4">{status}</p>
        <div className="w-full flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    </div>
  );
}
